import type { DownloadManagerDockBounds } from '@/lib/download-manager-docking';

const MOBILE_BREAKPOINT = 768;
/**
 * Composer lanes the floating voice dock keeps clear of on mobile. Surfaces
 * without their own marker (e.g. Connect) opt in with
 * `data-floating-dock-clearance`.
 */
export const VOICE_COMPOSER_SELECTOR =
  '[data-design-mobile-composer], [data-org-chat-input-multiline], [data-floating-dock-clearance]';

/** Reserve the measured composer lane only on a narrow viewport. */
export function getMobileVoiceComposerBottomInset({
  viewportWidth,
  boundary,
  composers,
  safeBottom = 0,
}: {
  viewportWidth: number;
  boundary: DownloadManagerDockBounds;
  composers: DownloadManagerDockBounds[];
  safeBottom?: number;
}): number {
  if (viewportWidth >= MOBILE_BREAKPOINT) return safeBottom;
  return composers.reduce((bottom, composer) => {
    if (
      composer.width <= 0 ||
      composer.height <= 0 ||
      composer.left >= boundary.left + boundary.width ||
      composer.left + composer.width <= boundary.left ||
      composer.top >= boundary.top + boundary.height ||
      composer.top + composer.height <= boundary.top
    )
      return bottom;
    return Math.max(bottom, boundary.top + boundary.height - composer.top);
  }, safeBottom);
}

function composerElements(): Element[] {
  return [
    ...new Set(
      [...document.querySelectorAll(VOICE_COMPOSER_SELECTOR)].map(
        (element) =>
          element.closest('[data-design-mobile-composer]') ??
          element.closest('form') ??
          element
      )
    ),
  ];
}

export function getVoiceComposerBounds(): DownloadManagerDockBounds[] {
  return composerElements().map((element) => element.getBoundingClientRect());
}

/** Watch composer mounts, growth and layout motion without observing the dock. */
export function observeVoiceComposerGeometry(update: () => void): () => void {
  let elements: Element[] = [];
  let frame: number | null = null;
  const schedule = () => {
    if (frame !== null) return;
    frame = window.requestAnimationFrame(() => {
      frame = null;
      update();
    });
  };
  const resize = new ResizeObserver(schedule);
  const geometry = new MutationObserver(schedule);
  const sync = () => {
    const next = composerElements();
    if (
      next.length === elements.length &&
      next.every((element, i) => element === elements[i])
    )
      return;
    resize.disconnect();
    geometry.disconnect();
    elements = next;
    for (const element of elements) {
      resize.observe(element);
      geometry.observe(
        element.closest('[data-design-mobile-home]') ?? element,
        {
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'class', 'data-mobile-keyboard-open'],
        }
      );
    }
    schedule();
  };
  const mounts = new MutationObserver(sync);
  mounts.observe(document.body, { childList: true, subtree: true });
  sync();
  window.addEventListener('scroll', schedule, { capture: true, passive: true });
  return () => {
    window.removeEventListener('scroll', schedule, true);
    mounts.disconnect();
    geometry.disconnect();
    resize.disconnect();
    if (frame !== null) window.cancelAnimationFrame(frame);
  };
}
